import React from "react";
import { getStatusEntries } from "../../utils/idLeadStatus";

/**
 * Human-readable labels for every IdLeadStatus value. Unknown keys fall back to
 * a Title-Cased version of the raw key (see labelForKey below).
 */
const STATUS_LABEL = {
  ID_ACKNOWLEDGEMENT_PENDING: "Acknowledgement pending",
  ID_DESIGN_REQUEST_ACKNOWLEDGED: "Design request acknowledged",
  ID_DESIGN_SUBMITTED: "Design submitted",
  BUYER_DESIGN_APPROVED: "Buyer approved",
  BUYER_DESIGN_FEEDBACK_PROVIDED: "Buyer feedback provided",
  BUYER_DESIGN_REJECTED: "Buyer rejected",
  ID_DESIGN_FEEDBACK_ACKNOWLEDGED: "Designer acknowledged feedback",
  ID_ACKNOWLEDGEMENT_EXPIRED: "Acknowledgement expired",
  ID_ITERATION_ACKNOWLEDGEMENT_EXPIRED: "Iteration ack expired",
  ID_DESIGN_DELIVERY_EXPIRED: "Delivery expired",
  DEVELOPER_PENDING: "Developer review pending",
  DEVELOPER_APPROVED: "Developer approved",
  DEVELOPER_REJECTED: "Developer rejected",
  LOST: "Lost",
};

// Negative outcomes — rendered with a rose/amber tone.
const NEGATIVE_KEYS = new Set([
  "ID_ACKNOWLEDGEMENT_EXPIRED",
  "ID_ITERATION_ACKNOWLEDGEMENT_EXPIRED",
  "ID_DESIGN_DELIVERY_EXPIRED",
  "BUYER_DESIGN_REJECTED",
  "DEVELOPER_REJECTED",
  "LOST",
]);

// Positive / progress outcomes — rendered with an emerald tone.
const POSITIVE_KEYS = new Set([
  "ID_DESIGN_REQUEST_ACKNOWLEDGED",
  "BUYER_DESIGN_APPROVED",
  "ID_DESIGN_FEEDBACK_ACKNOWLEDGED",
  "DEVELOPER_APPROVED",
]);

/** Title-Case a raw status key as a fallback label, e.g. FOO_BAR -> "Foo bar". */
const labelForKey = (key) => {
  if (STATUS_LABEL[key]) return STATUS_LABEL[key];
  if (!key || typeof key !== "string") return "Unknown";
  const spaced = key.replace(/_/g, " ").toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/** Format an ISO timestamp into a readable date + time, guarding invalid values. */
const formatTimestamp = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Per-entry tone classes for the dot + label. The most recent entry overrides
 * these with a flamingo accent regardless of its outcome category.
 */
const toneFor = (key, isLatest) => {
  if (isLatest) {
    return {
      dot: "bg-flamingo-500 ring-4 ring-flamingo-100",
      label: "text-flamingo-700 font-semibold",
    };
  }
  if (NEGATIVE_KEYS.has(key)) {
    return { dot: "bg-rose-400 ring-4 ring-rose-50", label: "text-rose-700" };
  }
  if (POSITIVE_KEYS.has(key)) {
    return { dot: "bg-emerald-400 ring-4 ring-emerald-50", label: "text-emerald-700" };
  }
  return { dot: "bg-slate-300 ring-4 ring-slate-50", label: "text-slate-700" };
};

/**
 * Presentational vertical timeline for an ID lead's status history.
 *
 * @param {object}  props.lead       Lead object carrying a `.status` JSONB array.
 * @param {string} [props.className] Optional wrapper classes.
 * @param {boolean}[props.compact]   Tighter spacing / smaller type when true.
 */
const LeadStatusTimeline = ({ lead, className = "", compact = false }) => {
  const entries = getStatusEntries(lead);

  if (entries.length === 0) {
    return (
      <p className={`text-sm text-slate-400 italic ${className}`.trim()}>
        No activity yet
      </p>
    );
  }

  const lastIndex = entries.length - 1;
  const gap = compact ? "pb-4" : "pb-6";

  return (
    <ol className={`relative ${className}`.trim()}>
      {entries.map((entry, index) => {
        const isLatest = index === lastIndex;
        const tone = toneFor(entry.key, isLatest);
        const formatted = formatTimestamp(entry.timestamp);
        return (
          <li
            key={`${entry.key}-${entry.timestamp}-${index}`}
            className={`relative flex gap-3 ${index === lastIndex ? "" : gap}`}
          >
            {/* Connector line (skip on the last entry) */}
            {index !== lastIndex && (
              <span
                aria-hidden="true"
                className="absolute left-[5px] top-3 -bottom-1 w-px bg-slate-200"
              />
            )}
            {/* Dot */}
            <span className="relative z-10 mt-1 flex-shrink-0">
              <span className={`block rounded-full ${compact ? "h-2 w-2" : "h-2.5 w-2.5"} ${tone.dot}`} />
            </span>
            {/* Content */}
            <div className="min-w-0 flex-1 -mt-0.5">
              <p className={`${compact ? "text-xs" : "text-sm"} ${tone.label}`}>
                {labelForKey(entry.key)}
              </p>
              {formatted && (
                <p className={`mt-0.5 ${compact ? "text-[10px]" : "text-xs"} text-slate-400 tabular-nums`}>
                  {formatted}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default LeadStatusTimeline;
